import React, { useRef, useEffect, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { getAllTimeOptions } from "../utils/timePickerUtils";

/**
 * TimePickerDropdown Component
 * A dropdown component for selecting time with hour, minute, and AM/PM columns
 */
const TimePickerDropdown = ({
  pickerType,
  inputValue,
  is24Hour,
  inputRef,
  position = "bottom",
  isOpen,
  timePickerSelection,
  setTimePickerSelection,
  timePickerRefs,
  isSettingFromDropdownRef,
  setTimePickerOpen,
  setTimePickerFilter,
  setIsTimeInputActive,
  manualChangeStartTime,
  manualChangeStartTime24,
  manualChangeEndTime,
  manualChangeEndTime24,
}) => {
  const options = getAllTimeOptions(is24Hour);
  const selection = timePickerSelection[pickerType];
  const hourColumnRef = useRef(null);
  const minuteColumnRef = useRef(null);
  const ampmColumnRef = useRef(null);
  const [portalStyle, setPortalStyle] = useState(null);

  // Portal to body + fixed position so parent overflow cannot clip the menu
  useEffect(() => {
    if (!isOpen) {
      setPortalStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const inputEl = inputRef?.current;
      if (!inputEl) return;

      const rect = inputEl.getBoundingClientRect();
      const dropdownWidth = is24Hour ? 143 : 190;
      const approxHeight = 280;
      const gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp =
        position === "top" ||
        (spaceBelow < approxHeight && spaceAbove > spaceBelow);

      const left = Math.max(
        8,
        Math.min(rect.left, window.innerWidth - dropdownWidth - 8)
      );

      if (openUp) {
        setPortalStyle({
          position: "fixed",
          top: "auto",
          bottom: Math.max(8, window.innerHeight - rect.top + gap),
          left,
          zIndex: 10000,
        });
      } else {
        setPortalStyle({
          position: "fixed",
          top: rect.bottom + gap,
          bottom: "auto",
          left,
          zIndex: 10000,
        });
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    // capture scroll from nested scroll parents (e.g. .content-outer)
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, inputRef, is24Hour, position]);
  const hasScrolledRef = useRef(false);
  const initialScrollValuesRef = useRef({ hour: null, minute: null, ampm: null });
  const scrollLockRef = useRef({ hour: false, minute: false, ampm: false });
  const unlockScrollRef = useRef({ hour: null, minute: null, ampm: null });
  const originalValueRef = useRef(null);

  // Parse current value to highlight selected items
  const parseCurrentTime = () => {
    if (!inputValue) return { hour: null, minute: null, ampm: null };

    const timeMatch = inputValue.match(/(\d{1,2})[:\s]+(\d{1,2})\s*(AM|PM)?/i);
    if (timeMatch) {
      const [, hour, minute, ampm] = timeMatch;
      return {
        hour: hour.padStart(2, "0"),
        minute: minute.padStart(2, "0"),
        ampm: ampm ? ampm.toUpperCase() : null,
      };
    }
    return { hour: null, minute: null, ampm: null };
  };

  const currentTime = parseCurrentTime();
  const selectedHour = selection?.hour || currentTime.hour;
  const selectedMinute = selection?.minute || currentTime.minute;
  const selectedAmpm = selection?.ampm || currentTime.ampm || (is24Hour ? null : "AM");

  // Scroll to selected values only when dropdown first opens (initial scroll)
  useEffect(() => {
    if (!isOpen) {
      // Reset scroll flag when dropdown closes
      hasScrolledRef.current = false;
      initialScrollValuesRef.current = { hour: null, minute: null, ampm: null };

      // Unlock any locked scrolls when dropdown closes
      if (unlockScrollRef.current.hour) {
        unlockScrollRef.current.hour();
        unlockScrollRef.current.hour = null;
      }
      if (unlockScrollRef.current.minute) {
        unlockScrollRef.current.minute();
        unlockScrollRef.current.minute = null;
      }
      if (unlockScrollRef.current.ampm) {
        unlockScrollRef.current.ampm();
        unlockScrollRef.current.ampm = null;
      }

      return;
    }

    // Only scroll once when dropdown first opens, not when values change
    if (hasScrolledRef.current) {
      return;
    }

    // Store initial values to prevent scrolling when they change
    initialScrollValuesRef.current = {
      hour: selectedHour,
      minute: selectedMinute,
      ampm: selectedAmpm,
    };

    hasScrolledRef.current = true;

    // Position scroll to show selected values (without locking scroll)
    // Scroll to initial selected hour
    if (initialScrollValuesRef.current.hour && hourColumnRef.current) {
      const hourIndex = options.hours.indexOf(initialScrollValuesRef.current.hour);
      if (hourIndex !== -1) {
        const hourElement = hourColumnRef.current.children[hourIndex];
        if (hourElement) {
          setTimeout(() => {
            // Check if refs are still valid before accessing properties
            if (!hourColumnRef.current || !hourElement) return;
            // Position scroll to center the element without using scrollIntoView
            const elementTop = hourElement.offsetTop;
            const elementHeight = hourElement.offsetHeight;
            const containerHeight = hourColumnRef.current.clientHeight;
            if (containerHeight) {
              const scrollTop = elementTop - (containerHeight / 2) + (elementHeight / 2);
              hourColumnRef.current.scrollTop = Math.max(0, scrollTop);
            }
          }, 100);
        }
      }
    }

    // Scroll to initial selected minute
    if (initialScrollValuesRef.current.minute && minuteColumnRef.current) {
      const minuteIndex = options.minutes.indexOf(initialScrollValuesRef.current.minute);
      if (minuteIndex !== -1) {
        const minuteElement = minuteColumnRef.current.children[minuteIndex];
        if (minuteElement) {
          setTimeout(() => {
            // Check if refs are still valid before accessing properties
            if (!minuteColumnRef.current || !minuteElement) return;
            // Position scroll to center the element without using scrollIntoView
            const elementTop = minuteElement.offsetTop;
            const elementHeight = minuteElement.offsetHeight;
            const containerHeight = minuteColumnRef.current.clientHeight;
            if (containerHeight) {
              const scrollTop = elementTop - (containerHeight / 2) + (elementHeight / 2);
              minuteColumnRef.current.scrollTop = Math.max(0, scrollTop);
            }
          }, 100);
        }
      }
    }

    // Scroll to initial selected AM/PM
    if (!is24Hour && initialScrollValuesRef.current.ampm && ampmColumnRef.current) {
      const ampmIndex = options.ampm.indexOf(initialScrollValuesRef.current.ampm);
      if (ampmIndex !== -1) {
        const ampmElement = ampmColumnRef.current.children[ampmIndex];
        if (ampmElement) {
          setTimeout(() => {
            // Check if refs are still valid before accessing properties
            if (!ampmColumnRef.current || !ampmElement) return;
            // Position scroll to center the element without using scrollIntoView
            const elementTop = ampmElement.offsetTop;
            const elementHeight = ampmElement.offsetHeight;
            const containerHeight = ampmColumnRef.current.clientHeight;
            if (containerHeight) {
              const scrollTop = elementTop - (containerHeight / 2) + (elementHeight / 2);
              ampmColumnRef.current.scrollTop = Math.max(0, scrollTop);
            }
          }, 100);
        }
      }
    }
  }, [isOpen, selectedHour, selectedMinute, selectedAmpm, is24Hour, options]);

  // Store original time on open so Cancel can revert
  useEffect(() => {
    if (isOpen && originalValueRef.current === null) {
      originalValueRef.current = inputValue;
    }
    if (!isOpen) {
      originalValueRef.current = null;
    }
  }, [isOpen, inputValue]);

  // Helper function to apply a selection immediately to the input
  const applySelectionToInput = useCallback((newSelection) => {
    const hour = newSelection.hour || (is24Hour ? "00" : "01");
    const minute = newSelection.minute || "00";
    const ampm = is24Hour ? null : (newSelection.ampm || "AM");
    const is24HourFormat = pickerType.includes("24");
    if (is24HourFormat) {
      const formatted = `${hour}:${minute}`;
      if (pickerType === "start24") manualChangeStartTime24(formatted);
      else if (pickerType === "end24") manualChangeEndTime24(formatted);
    } else {
      const formatted = `${hour}:${minute} ${ampm}`;
      if (pickerType === "start12") manualChangeStartTime(formatted);
      else if (pickerType === "end12") manualChangeEndTime(formatted);
    }
  }, [is24Hour, pickerType, manualChangeStartTime, manualChangeStartTime24, manualChangeEndTime, manualChangeEndTime24]);

  // Helper function to lock scroll position - more aggressive approach
  const lockScroll = (columnType, containerRef) => {
    if (!containerRef?.current) return null;

    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    scrollLockRef.current[columnType] = true;

    // Store original scroll position (keep overflow unchanged to avoid scrollbar flicker)
    container.dataset.lockedScrollTop = scrollTop;

    // Prevent scrollIntoView on all children and the container itself
    const preventScrollIntoView = (element) => {
      if (element && element.scrollIntoView) {
        if (!element._originalScrollIntoView) {
          element._originalScrollIntoView = element.scrollIntoView;
        }
        element.scrollIntoView = () => { };
      }
    };

    // Prevent on all children
    Array.from(container.children).forEach(preventScrollIntoView);
    // Prevent on container itself
    preventScrollIntoView(container);

    // Aggressive scroll prevention - multiple event listeners
    const preventScroll = (e) => {
      if (scrollLockRef.current[columnType]) {
        const lockedScrollTop = parseFloat(container.dataset.lockedScrollTop || scrollTop);
        container.scrollTop = lockedScrollTop;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    // Add multiple scroll prevention mechanisms
    container.addEventListener('scroll', preventScroll, { passive: false, capture: true });
    container.addEventListener('wheel', preventScroll, { passive: false, capture: true });
    container.addEventListener('touchmove', preventScroll, { passive: false, capture: true });
    container._scrollPreventer = preventScroll;

    // Continuously restore scroll position while locked (runs every frame)
    let restoreFrameId;
    const restoreScroll = () => {
      if (scrollLockRef.current[columnType] && container) {
        const lockedScrollTop = parseFloat(container.dataset.lockedScrollTop || scrollTop);
        if (Math.abs(container.scrollTop - lockedScrollTop) > 0.5) {
          container.scrollTop = lockedScrollTop;
        }
        restoreFrameId = requestAnimationFrame(restoreScroll);
      }
    };
    restoreFrameId = requestAnimationFrame(restoreScroll);
    container._scrollRestoreFrame = restoreFrameId;

    return () => {
      scrollLockRef.current[columnType] = false;

      // Remove event listeners
      if (container._scrollPreventer) {
        container.removeEventListener('scroll', container._scrollPreventer, { capture: true });
        container.removeEventListener('wheel', container._scrollPreventer, { capture: true });
        container.removeEventListener('touchmove', container._scrollPreventer, { capture: true });
        delete container._scrollPreventer;
      }

      // Cancel animation frame
      if (container._scrollRestoreFrame) {
        cancelAnimationFrame(container._scrollRestoreFrame);
        delete container._scrollRestoreFrame;
      }

      // Restore scrollIntoView on all children
      Array.from(container.children).forEach((child) => {
        if (child._originalScrollIntoView) {
          child.scrollIntoView = child._originalScrollIntoView;
          delete child._originalScrollIntoView;
        }
      });

      // Restore scrollIntoView on container
      if (container._originalScrollIntoView) {
        container.scrollIntoView = container._originalScrollIntoView;
        delete container._originalScrollIntoView;
      }

      // Restore scroll position
      if (container.dataset.lockedScrollTop) {
        requestAnimationFrame(() => {
          container.scrollTop = parseFloat(container.dataset.lockedScrollTop);
          delete container.dataset.lockedScrollTop;
        });
      }
    };
  };

  // Handle column selection - live-update input immediately on each selection
  const handleColumnSelect = (type, value, element) => {
    // Prevent any automatic scrolling
    if (element) {
      const originalScrollIntoView = element.scrollIntoView;
      element.scrollIntoView = () => {};
      setTimeout(() => {
        if (element) element.scrollIntoView = originalScrollIntoView;
      }, 200);
    }

    isSettingFromDropdownRef.current = true;

    const newSelection = {
      hour: selection?.hour || currentTime.hour || (is24Hour ? "00" : "01"),
      minute: selection?.minute || currentTime.minute || "00",
      ampm: is24Hour ? null : (selection?.ampm || currentTime.ampm || "AM"),
      [type]: value,
    };

    setTimePickerSelection(prev => ({
      ...prev,
      [pickerType]: newSelection,
    }));

    // Live-apply the selection to the time input immediately
    applySelectionToInput(newSelection);

    setTimeout(() => {
      isSettingFromDropdownRef.current = false;
    }, 100);
  };

  // Confirm - time already applied live, just close
  const handleOk = useCallback(() => {
    setTimePickerOpen(prev => ({ ...prev, [pickerType]: false }));
    setTimePickerFilter(prev => ({ ...prev, [pickerType]: "" }));
    setIsTimeInputActive(false);
    originalValueRef.current = null;
  }, [pickerType, setTimePickerOpen, setTimePickerFilter, setIsTimeInputActive]);

  // Cancel - revert to original time before picker was opened
  const handleCancel = () => {
    if (originalValueRef.current !== null) {
      const is24HourFormat = pickerType.includes("24");
      if (is24HourFormat) {
        if (pickerType === "start24") manualChangeStartTime24(originalValueRef.current);
        else if (pickerType === "end24") manualChangeEndTime24(originalValueRef.current);
      } else {
        if (pickerType === "start12") manualChangeStartTime(originalValueRef.current);
        else if (pickerType === "end12") manualChangeEndTime(originalValueRef.current);
      }
    }
    setTimePickerOpen(prev => ({ ...prev, [pickerType]: false }));
    setTimePickerFilter(prev => ({ ...prev, [pickerType]: "" }));
    setTimePickerSelection(prev => ({ ...prev, [pickerType]: null }));
    setIsTimeInputActive(false);
    originalValueRef.current = null;
  };

  // Enter key confirms and closes
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleOk();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleOk]);

  if (!isOpen || !portalStyle) return null;

  return createPortal(
    <div
      className="time-picker-dropdown"
      ref={timePickerRefs[pickerType]}
      style={{
        ...portalStyle,
        backgroundColor: "white",
        border: "1px solid #ddd",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        minWidth: is24Hour ? "143px" : "190px",
        maxWidth: is24Hour ? "190px" : "190px",
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
    >
      {/* Time selection columns */}
      <div 
        className="hours-min-outer"
        style={{
        display: "flex",
        maxHeight: "220px",
        overflow: "hidden",
        position: "relative",
        paddingTop: "4px",
      }}>
        {/* Hours Column */}
        <div
          ref={hourColumnRef}
          className="hours-column"
          style={{
            flex: 1,
            overflowY: "scroll",
            overflowX: "hidden",
            scrollbarGutter: "stable",
            borderRight: "1px solid #D8DDE1",
            maxHeight: "250px",
            padding: "4px 0",
          }}
          onWheel={(e) => {
            e.stopPropagation();
          }}
          onScroll={(e) => {
            // Prevent scrolling if scroll is locked
            if (scrollLockRef.current.hour && hourColumnRef.current) {
              const lockedScrollTop = parseFloat(hourColumnRef.current.dataset.lockedScrollTop || 0);
              hourColumnRef.current.scrollTop = lockedScrollTop;
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              return false;
            }
            e.stopPropagation();
          }}
          onClick={(e) => {
            // Prevent any automatic scrolling when clicking in the column
            e.stopPropagation();
          }}
        >
          {options.hours.map((hour) => (
            <div
              key={hour}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault(); // Prevent default to avoid any side effects
                // Prevent the mousedown from bubbling to document level
                if (e.nativeEvent) {
                  e.nativeEvent.stopImmediatePropagation();
                }

                // Lock scroll position immediately on mousedown
                if (!unlockScrollRef.current.hour) {
                  unlockScrollRef.current.hour = lockScroll('hour', hourColumnRef);
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Prevent the click from bubbling to document level
                if (e.nativeEvent) {
                  e.nativeEvent.stopImmediatePropagation();
                }
                // Prevent automatic scrolling
                e.preventDefault();

                const clickedElement = e.currentTarget;

                // Ensure scroll is locked (in case mousedown didn't fire)
                if (!unlockScrollRef.current.hour) {
                  unlockScrollRef.current.hour = lockScroll('hour', hourColumnRef);
                }

                // Handle the selection
                handleColumnSelect("hour", hour, clickedElement);

                // Unlock scroll after state update completes
                setTimeout(() => {
                  if (unlockScrollRef.current.hour) {
                    unlockScrollRef.current.hour();
                    unlockScrollRef.current.hour = null;
                  }
                }, 150);
              }}
              style={{
                padding: "4px 5px",
                cursor: "pointer",
                backgroundColor: selectedHour === hour ? "#405BFF" : "transparent",
                color: selectedHour === hour ? "#fff" : "#282828",
                fontSize: "15px",
                borderRadius: "4px",
                margin: "0 14px",
                fontWeight: "300",
                textAlign: "center",
                userSelect: "none",
                marginBottom: "14px"
              }}
              onMouseEnter={(e) => {
                if (selectedHour !== hour) {
                  e.target.style.backgroundColor = "#96A8BA36";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedHour !== hour) {
                  e.target.style.backgroundColor = "transparent";
                }
              }}
            >
              {hour}
            </div>
          ))}
        </div>

        {/* Minutes Column */}
        <div
          ref={minuteColumnRef}
          className="minutes-column"
          style={{
            flex: 1,
            overflowY: "scroll",
            overflowX: "hidden",
            scrollbarGutter: "stable",
            borderRight: is24Hour ? "none" : "1px solid #D8DDE1",
            maxHeight: "250px",
            padding: "4px 0",
          }}
          onWheel={(e) => {
            e.stopPropagation();
          }}
          onScroll={(e) => {
            // Prevent scrolling if scroll is locked
            if (scrollLockRef.current.minute && minuteColumnRef.current) {
              const lockedScrollTop = parseFloat(minuteColumnRef.current.dataset.lockedScrollTop || 0);
              minuteColumnRef.current.scrollTop = lockedScrollTop;
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              return false;
            }
            e.stopPropagation();
          }}
          onClick={(e) => {
            // Prevent any automatic scrolling when clicking in the column
            e.stopPropagation();
          }}
        >
          {options.minutes.map((minute) => (
            <div
              key={minute}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault(); // Prevent default to avoid any side effects
                // Prevent the mousedown from bubbling to document level
                if (e.nativeEvent) {
                  e.nativeEvent.stopImmediatePropagation();
                }

                // Lock scroll position immediately on mousedown
                if (!unlockScrollRef.current.minute) {
                  unlockScrollRef.current.minute = lockScroll('minute', minuteColumnRef);
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Prevent the click from bubbling to document level
                if (e.nativeEvent) {
                  e.nativeEvent.stopImmediatePropagation();
                }
                // Prevent automatic scrolling
                e.preventDefault();

                const clickedElement = e.currentTarget;

                // Ensure scroll is locked (in case mousedown didn't fire)
                if (!unlockScrollRef.current.minute) {
                  unlockScrollRef.current.minute = lockScroll('minute', minuteColumnRef);
                }

                // Handle the selection
                handleColumnSelect("minute", minute, clickedElement);

                // Unlock scroll after state update completes
                setTimeout(() => {
                  if (unlockScrollRef.current.minute) {
                    unlockScrollRef.current.minute();
                    unlockScrollRef.current.minute = null;
                  }
                }, 150);
              }}
              style={{
                padding: "4px 5px",
                cursor: "pointer",
                backgroundColor: selectedMinute === minute ? "#405BFF" : "transparent",
                color: selectedMinute === minute ? "#fff" : "#282828",
                fontSize: "15px",
                borderRadius: "4px",
                margin: "0 14px",
                fontWeight: "300",
                textAlign: "center",
                userSelect: "none",
                marginBottom: "14px",
              }}

              onMouseEnter={(e) => {
                if (selectedMinute !== minute) {
                  e.target.style.backgroundColor = "#f5f5f5";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedMinute !== minute) {
                  e.target.style.backgroundColor = "transparent";
                }
              }}
            >
              {minute}
            </div>
          ))}
        </div>

        {/* AM/PM Column (only for 12hr format) */}
        {!is24Hour && (
          <div
            ref={ampmColumnRef}
            className="am-pm-column"
            style={{
              flex: 0.8,
              overflowY: "scroll",
              overflowX: "hidden",
              scrollbarGutter: "stable",
              maxHeight: "250px",
              padding: "4px 0",
            }}
            onWheel={(e) => {
              e.stopPropagation();
            }}
            onScroll={(e) => {
              // Prevent scrolling if scroll is locked
              if (scrollLockRef.current.ampm && ampmColumnRef.current) {
                const lockedScrollTop = parseFloat(ampmColumnRef.current.dataset.lockedScrollTop || 0);
                ampmColumnRef.current.scrollTop = lockedScrollTop;
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                return false;
              }
              e.stopPropagation();
            }}
            onClick={(e) => {
              // Prevent scrolling when clicking inside the column
              e.stopPropagation();
            }}
          >
            {options.ampm.map((ampm) => (
              <div
                key={ampm}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault(); // Prevent default to avoid any side effects
                  // Prevent the mousedown from bubbling to document level
                  if (e.nativeEvent) {
                    e.nativeEvent.stopImmediatePropagation();
                  }

                  // Lock scroll position immediately on mousedown
                  if (!unlockScrollRef.current.ampm) {
                    unlockScrollRef.current.ampm = lockScroll('ampm', ampmColumnRef);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Prevent the click from bubbling to document level
                  if (e.nativeEvent) {
                    e.nativeEvent.stopImmediatePropagation();
                  }
                  // Prevent automatic scrolling
                  e.preventDefault();

                  const clickedElement = e.currentTarget;

                  // Ensure scroll is locked (in case mousedown didn't fire)
                  if (!unlockScrollRef.current.ampm) {
                    unlockScrollRef.current.ampm = lockScroll('ampm', ampmColumnRef);
                  }

                  // Handle the selection
                  handleColumnSelect("ampm", ampm, clickedElement);

                  // Unlock scroll after state update completes
                  setTimeout(() => {
                    if (unlockScrollRef.current.ampm) {
                      unlockScrollRef.current.ampm();
                      unlockScrollRef.current.ampm = null;
                    }
                  }, 150);
                }}
                style={{
                  padding: "4px 5px",
                  cursor: "pointer",
                  backgroundColor: selectedAmpm === ampm ? "#405BFF" : "#E8ECF0",
                  color: selectedAmpm === ampm ? "#fff" : "#282828",
                  fontSize: "15px",
                  textAlign: "center",
                  borderRadius: "6px",
                  margin: "0 10px",
                  fontWeight: "300",
                  userSelect: "none",
                  marginBottom: "14px"
                }}


                onMouseEnter={(e) => {
                  if (selectedAmpm !== ampm) {
                    e.target.style.backgroundColor = "#D0D7DE";
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedAmpm !== ampm) {
                    e.target.style.backgroundColor = "#E8ECF0";
                  }
                }}
              >
                {ampm}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cancel button — reverts to original time */}
      <div
        className="time-select-bottom"
        style={{
          height: "33px",
          padding: "0 25px",
          borderTop: "1px solid #D8DDE1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "0 0 8px 8px",
          background: "#FBFBFD",
        }}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleCancel();
          }}
          className="time-select-cancel"
          style={{
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: "14px",
            lineHeight: "17.08px",
            color: "#282828",
            padding: "4px 16px",
            borderRadius: "4px",
          }}
          onMouseEnter={(e) => { e.target.style.backgroundColor = "#f0f0f0"; }}
          onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; }}
        >
          Cancel
        </button>
      </div>
    </div>,
    document.body
  );
};

export default TimePickerDropdown;
